import { Symptom } from '@enums/symptoms';
import { ConsultationRequestAdditionalInfo } from '@models/consultation-request/consultation-request-additional-info';
import { DateData } from '@models/date-data';
import { DateSelection } from '@models/date-selection';

export class ConsultationRequestHelper {
  private static readonly maxAgeForDefaultHeightWeight = 20;

  /**
   * Checks if the patient is under the minimum prescription age based on their date of birth.
   *
   * @param {string} dateOfBirth the patient's date of birth in "YYYY-MM-DD" format
   */
  public static isPatientUnderMinimumPrescriptionAge(dateOfBirth: string): boolean {
    return this.getPatientAge(dateOfBirth) < this.maxAgeForDefaultHeightWeight;
  }

  /**
   * Gets default additional info (e.g. height/weight) to inject into a consultation request.
   */
  public static getFemaleCondomDefaultAdditionalInfo(): ConsultationRequestAdditionalInfo {
    return { height: { feet: 5, inches: 4 }, weight: 140 };
  }

  /**
   * Get a string of symptoms from the symptoms form group.
   *
   * @param symptoms symptoms form group
   * @returns a string with the readable name of each symptom
   */
  public static getSymptomsArrayFromValues(symptoms: Object): string[] {
    return Object.keys(symptoms)
      .map((symptom) => ({
        name: Object.keys(Symptom).find((key) => Symptom[key] === symptom),
        selected: symptoms[symptom],
      }))
      .filter((symptom) => symptom.selected)
      .map((symptom) => symptom.name);
  }

  /**
   * Get a string with this format '1995-06-04' from a date object like the following:
   * { day: '6', month: '4', year: '1995' }
   *
   * @param date object containing the day, month, and year as strings
   * @returns string with the date
   */
  public static getDateDataString(date: DateData): string {
    if (!date?.day || !date?.month || !date?.year) {
      return null;
    }

    return `${date.year}-${('0' + date.month).slice(-2)}-${('0' + date.day).slice(-2)}`;
  }

  /**
   * Convert each number in an object to a string.
   *
   * @param object the original object
   * @returns the object with the numbers converted to strings
   */
  public static convertObjectNumbersToStrings(object: Object): Object {
    Object.keys(object).forEach((key) => {
      if (object[key] !== null && !isNaN(+object[key])) {
        object[key] = (+object[key] as number).toString();
      }
    });

    return object;
  }

  /**
   * Gets the patient's current age from their date of birth.
   *
   * @param dateOfBirth the patient's date of birth in "YYYY-MM-DD" format
   * @returns the patient's age in years
   */
  private static getPatientAge(dateOfBirth: string): number {
    const [year, month, day] = dateOfBirth.split('-').map(Number);

    return new DateSelection(month, day, year).getAge();
  }
}
